import React, { useMemo, useState } from "react";
import {
  FaArrowRightLong,
  FaBuildingShield,
  FaClipboardCheck,
  FaFileCircleCheck,
  FaFileContract,
  FaLink,
  FaListCheck,
  FaListOl,
  FaScaleBalanced
} from "react-icons/fa6";
import naicsComplianceData from "../data/naicsComplianceData.json";
import {
  resolveComplianceRecord,
  validateNaicsCode
} from "../utils/naicsComplianceUtils";
import "../styles/NaicsComplianceChecker.css";

function listKey(title) {
  return title.toLowerCase().replace(/\s+/g, "-");
}

function isPreCompletedRegistration(registration) {
  const text = registration.toLowerCase();
  return text.includes("business registration") || text === "ein" || text.includes(" ein");
}

function inferTags(value) {
  const text = value.toLowerCase();
  const tags = new Set();

  if (text.includes("fda")) tags.add("fda");
  if (text.includes("usda")) tags.add("usda");
  if (text.includes("osha")) tags.add("osha");
  if (text.includes("epa")) tags.add("epa");
  if (text.includes("health")) tags.add("health");
  if (text.includes("meat") || text.includes("haccp")) tags.add("meat");
  if (text.includes("label")) tags.add("labeling");
  if (text.includes("sanitation") || text.includes("gmp")) tags.add("sanitation");
  if (text.includes("water") || text.includes("wastewater") || text.includes("environment")) {
    tags.add("environment");
  }
  if (text.includes("inspection")) tags.add("inspection");
  if (text.includes("permit") || text.includes("license")) tags.add("licensing");
  if (text.includes("registration") || text.includes("ein")) tags.add("registration");
  if (text.includes("document") || text.includes("record") || text.includes("plan")) tags.add("documentation");
  return Array.from(tags);
}

function hasRelatedTags(a, b) {
  if (!a?.length || !b?.length) return false;
  return a.some((tag) => b.includes(tag));
}

function normalizeText(value) {
  return (value || "").toLowerCase().replace(/\s+/g, " ").trim();
}

function findItemLinks(result, type, label) {
  const candidates = result?.item_links?.[type];
  if (!Array.isArray(candidates) || !label) return null;
  const target = normalizeText(label);
  return (
    candidates.find((item) => normalizeText(item.name) === target) ||
    null
  );
}

const PART_LABELS = {
  application_flow: "Application flow",
  journey: "Compliance journey",
  agencies: "Government agencies",
  regulations: "Regulations",
  documents: "Documentation checklist",
  registrations: "Required registrations",
  permits: "Required permits",
  next_steps: "Next steps"
};

const DEFAULT_PART_LINKS = {
  application_flow: {
    online: "https://www.sba.gov/business-guide/launch-your-business/apply-licenses-permits",
    online_label: "Apply online"
  },
  journey: {
    online: "https://www.sba.gov/business-guide/manage-your-business/stay-legally-compliant",
    online_label: "Read details"
  },
  agencies: {
    online: "https://www.usa.gov/agencies",
    online_label: "Read details"
  },
  regulations: {
    online: "https://www.ecfr.gov/",
    online_label: "Read details"
  },
  documents: {
    online: "https://www.fda.gov/food/food-safety-modernization-act-fsma",
    online_label: "Read details"
  },
  registrations: {
    online: "https://www.irs.gov/businesses/small-businesses-self-employed/employer-id-numbers",
    online_label: "Apply online"
  },
  permits: {
    online: "https://www.sba.gov/business-guide/launch-your-business/apply-licenses-permits",
    online_label: "Apply online"
  },
  next_steps: {
    online: "https://www.sba.gov/business-guide/manage-your-business/stay-legally-compliant",
    online_label: "Read details"
  }
};

function buildGovPdfSearchUrl(businessType, partKey) {
  const partLabel = PART_LABELS[partKey] || "compliance";
  const query = `${businessType || "food business"} ${partLabel} site:gov filetype:pdf`;
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}

function NaicsComplianceChecker() {
  const [naicsCode, setNaicsCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [matchType, setMatchType] = useState("");
  const [checkedDocs, setCheckedDocs] = useState({});
  const [journeyAnchor, setJourneyAnchor] = useState(null);

  const normalizedCode = useMemo(() => naicsCode.trim(), [naicsCode]);

  const resetResult = () => {
    setResult(null);
    setMatchType("");
    setCheckedDocs({});
    setJourneyAnchor(null);
  };

  const handleSearch = async () => {
    const validationError = validateNaicsCode(normalizedCode);
    if (validationError) {
      setError(validationError);
      resetResult();
      return;
    }

    setError("");
    setLoading(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 200));
      const resolved = resolveComplianceRecord(normalizedCode, naicsComplianceData);

      if (resolved.source === "unsupported") {
        setError("NAICS not supported for U.S. Food Industry compliance suggestions.");
        resetResult();
        return;
      }

      setResult(resolved.data);
      setMatchType(resolved.source);
      setCheckedDocs(
        (resolved.data.documents || []).reduce((acc, doc) => {
          acc[doc] = false;
          return acc;
        }, {})
      );
      setJourneyAnchor(null);
    } catch (err) {
      console.error("Compliance lookup failed:", err);
      setError("Something went wrong while generating compliance suggestions.");
      resetResult();
    } finally {
      setLoading(false);
    }
  };

  const toggleDoc = (doc) => {
    setCheckedDocs((prev) => ({
      ...prev,
      [doc]: !prev[doc]
    }));
  };

  const markAllDocs = (checked) => {
    if (!result) return;
    const nextState = {};
    result.documents.forEach((doc) => {
      nextState[doc] = checked;
    });
    setCheckedDocs(nextState);
  };

  const riskClass = (result?.risk_level || "").toLowerCase();
  const totalDocs = result?.documents?.length || 0;
  const completedDocs = Object.values(checkedDocs).filter(Boolean).length;
  const completionPercent = totalDocs
    ? Math.round((completedDocs / totalDocs) * 100)
    : 0;

  const applicationFlow = result
    ? [
        ...result.registrations.map((registration, index) => ({
          id: `registration-${index}-${registration}`,
          label: registration,
          type: "registration",
          stepLabel: `R${index + 1}`,
          done: isPreCompletedRegistration(registration)
        })),
        ...result.permits.map((permit, index) => ({
          id: `permit-${index}-${permit}`,
          label: permit,
          type: "permit",
          stepLabel: `P${index + 1}`,
          done: false
        }))
      ]
    : [];

  const agencyTone = (agency) => {
    if (agency.includes("USDA")) return "tone-green";
    if (agency.includes("FDA")) return "tone-blue";
    if (agency.includes("OSHA")) return "tone-amber";
    if (agency.includes("EPA")) return "tone-teal";
    if (agency.toLowerCase().includes("health")) return "tone-purple";
    return "tone-slate";
  };

  const journeyColumns = result
    ? {
        agencies: result.agencies.map((v) => ({ value: v, tags: inferTags(v), type: "agency" })),
        regulations: result.regulations.map((v) => ({ value: v, tags: inferTags(v), type: "regulation" })),
        licensing: [...result.registrations, ...result.permits].map((v) => ({
          value: v,
          tags: inferTags(v),
          type: "licensing"
        })),
        documents: result.documents.map((v) => ({ value: v, tags: inferTags(v), type: "document" }))
      }
    : null;

  const isRelatedToAnchor = (item) => {
    if (!journeyAnchor) return true;
    if (journeyAnchor.value === item.value && journeyAnchor.type === item.type) return true;
    return hasRelatedTags(journeyAnchor.tags, item.tags);
  };

  const itemStateClass = (item) => {
    if (!journeyAnchor) return "";
    if (journeyAnchor.value === item.value && journeyAnchor.type === item.type) return "selected";
    return isRelatedToAnchor(item) ? "related" : "dimmed";
  };

  const renderPartLinks = (partKey) => {
    if (!result) return null;
    const configuredLinks = result.part_links?.[partKey] || {};
    const defaultLinks = DEFAULT_PART_LINKS[partKey] || {};
    const onlineUrl = configuredLinks.online || defaultLinks.online;
    const onlineLabel = configuredLinks.online_label || defaultLinks.online_label || "Read details";
    const displayOnlineLabel =
      onlineLabel.toLowerCase() === "apply online" ? "Official website" : onlineLabel;
    const pdfUrl = configuredLinks.pdf || buildGovPdfSearchUrl(result.business_type, partKey);

    return (
      <div className="section-resource-links">
        <a href={pdfUrl} target="_blank" rel="noreferrer">
          PDF guide
        </a>
        {onlineUrl ? (
          <a href={onlineUrl} target="_blank" rel="noreferrer">
            {displayOnlineLabel}
          </a>
        ) : null}
      </div>
    );
  };

  const renderItemApplyLink = (type, label) => {
    if (!result) return null;
    const links = findItemLinks(result, type, label);
    if (!links?.apply) return null;
    return (
      <a href={links.apply} target="_blank" rel="noreferrer">
        Official website
      </a>
    );
  };

  return (
    <div className="naics-compliance-checker">
      <div className="naics-checker-header">
        <h3 className="naics-checker-title">NAICS Compliance Checker</h3>
        <p className="naics-checker-subtitle">
          Simple compliance view for U.S. food-industry NAICS codes.
        </p>
      </div>

      <div className="naics-input-row">
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          className="naics-input"
          placeholder="Enter 6-digit NAICS code"
          value={naicsCode}
          onChange={(e) => {
            const nextValue = e.target.value.replace(/\D/g, "");
            setNaicsCode(nextValue);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
          aria-label="NAICS code"
        />
        <button
          className="naics-button"
          type="button"
          onClick={handleSearch}
          disabled={loading}
        >
          {loading ? "Checking..." : "Check"}
        </button>
      </div>

      {error ? <div className="naics-message error">{error}</div> : null}

      {!error && loading ? (
        <div className="naics-message info">Loading compliance data...</div>
      ) : null}

      {result ? (
        <div className="naics-result-card">
          <div className="naics-result-header">
            <div>
              <h4 className="naics-result-title">{result.business_type}</h4>
              <p className="naics-result-meta">
                NAICS: {normalizedCode} | Category: {result.category} | Inspection: {result.inspection_type}
              </p>
              <p className="result-note">
                {matchType === "exact"
                  ? "Exact NAICS match"
                  : "Category default applied (prefix match)"}
              </p>
            </div>
            <span className={`risk-badge ${riskClass}`}>{result.risk_level}</span>
          </div>

          <section className="compact-progress">
            <div className="compact-progress-head">
              <span>Documentation Progress</span>
              <strong>{completedDocs}/{totalDocs}</strong>
            </div>
            <div className="progress-rail">
              <div
                className="progress-fill"
                style={{ width: `${completionPercent}%` }}
              />
            </div>
          </section>

          <div className="result-layout-simple">
            <section className="naics-section flow-panel flow-card">
              <h4>
                <FaListOl className="title-icon" />
                Application Flow
              </h4>
              {renderPartLinks("application_flow")}
              <p className="flow-note">Business Registration and EIN are marked done.</p>
              <div className="flow-list">
                {applicationFlow.map((item) => (
                  <div
                    key={listKey(item.id)}
                    className={`flow-item ${item.type} ${item.done ? "done" : "pending"}`}
                  >
                    <span className="flow-badge">{item.stepLabel}</span>
                    <span className="flow-label">{item.label}</span>
                    {renderItemApplyLink(
                      item.type === "registration" ? "registrations" : "permits",
                      item.label
                    )}
                    <span className={`flow-status ${item.done ? "done" : "pending"}`}>
                      {item.done ? "Done" : "Pending"}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section className="naics-section journey-panel">
              <h4>
                <FaLink className="title-icon" />
                Compliance Journey (Related View)
              </h4>
              {renderPartLinks("journey")}
              <p className="flow-note">
                Click an item to highlight related items across categories.
              </p>
              <div className="journey-grid">
                <div className="journey-col">
                  <p className="journey-col-title">Agencies</p>
                  {journeyColumns.agencies.map((item) => (
                    <button
                      key={listKey(`agency-${item.value}`)}
                      type="button"
                      className={`journey-item ${itemStateClass(item)}`}
                      onClick={() => setJourneyAnchor(item)}
                    >
                      {item.value}
                    </button>
                  ))}
                </div>

                <div className="journey-arrow"><FaArrowRightLong /></div>

                <div className="journey-col">
                  <p className="journey-col-title">Regulations</p>
                  {journeyColumns.regulations.map((item) => (
                    <button
                      key={listKey(`reg-${item.value}`)}
                      type="button"
                      className={`journey-item ${itemStateClass(item)}`}
                      onClick={() => setJourneyAnchor(item)}
                    >
                      {item.value}
                    </button>
                  ))}
                </div>

                <div className="journey-arrow"><FaArrowRightLong /></div>

                <div className="journey-col">
                  <p className="journey-col-title">Registrations & Permits</p>
                  {journeyColumns.licensing.map((item) => (
                    <button
                      key={listKey(`lic-${item.value}`)}
                      type="button"
                      className={`journey-item ${itemStateClass(item)}`}
                      onClick={() => setJourneyAnchor(item)}
                    >
                      {item.value}
                    </button>
                  ))}
                </div>

                <div className="journey-arrow"><FaArrowRightLong /></div>

                <div className="journey-col">
                  <p className="journey-col-title">Documents</p>
                  {journeyColumns.documents.map((item) => (
                    <button
                      key={listKey(`doc-${item.value}`)}
                      type="button"
                      className={`journey-item ${itemStateClass(item)}`}
                      onClick={() => setJourneyAnchor(item)}
                    >
                      {item.value}
                    </button>
                  ))}
                </div>
              </div>
              {journeyAnchor ? (
                <button
                  type="button"
                  className="mini-btn mini-btn-light journey-reset"
                  onClick={() => setJourneyAnchor(null)}
                >
                  Clear highlight
                </button>
              ) : null}
            </section>

            <section className="naics-section agency-card">
              <h4>
                <FaBuildingShield className="title-icon" />
                Government Agencies
              </h4>
              {renderPartLinks("agencies")}
              <ul className="agency-list">
                {result.agencies.map((agency) => (
                  <li key={listKey(agency)} className={`agency-chip ${agencyTone(agency)}`}>
                    <FaBuildingShield />
                    <span>{agency}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="naics-section regulation-card">
              <h4>
                <FaScaleBalanced className="title-icon" />
                Regulations
              </h4>
              {renderPartLinks("regulations")}
              <ul className="naics-list">
                {result.regulations.map((regulation) => (
                  <li key={listKey(regulation)} className="pretty-li">
                    <span className="li-icon regulation">
                      <FaScaleBalanced />
                    </span>
                    <span>{regulation}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="naics-section docs-card">
              <h4>
                <FaListCheck className="title-icon" />
                Documentation Checklist
              </h4>
              {renderPartLinks("documents")}
              <div className="checklist-actions">
                <button
                  type="button"
                  className="mini-btn"
                  onClick={() => markAllDocs(true)}
                >
                  Mark all done
                </button>
                <button
                  type="button"
                  className="mini-btn mini-btn-light"
                  onClick={() => markAllDocs(false)}
                >
                  Reset
                </button>
              </div>
              <ul className="docs-list">
                {result.documents.map((doc) => (
                  <li key={listKey(doc)}>
                    <input
                      id={listKey(doc)}
                      type="checkbox"
                      checked={!!checkedDocs[doc]}
                      onChange={() => toggleDoc(doc)}
                    />
                    <label htmlFor={listKey(doc)}>{doc}</label>
                  </li>
                ))}
              </ul>
            </section>

            <section className="naics-section registrations-card">
              <h4>
                <FaFileContract className="title-icon" />
                Required Registrations
              </h4>
              {renderPartLinks("registrations")}
              <ul className="naics-list">
                {result.registrations.map((registration) => (
                  <li key={listKey(registration)} className="pretty-li">
                    <span className="li-icon registration">
                      <FaFileContract />
                    </span>
                    <span>{registration}</span>
                    {renderItemApplyLink("registrations", registration)}
                  </li>
                ))}
              </ul>
            </section>

            <section className="naics-section permits-card">
              <h4>
                <FaFileCircleCheck className="title-icon" />
                Required Permits
              </h4>
              {renderPartLinks("permits")}
              <ul className="naics-list">
                {result.permits.map((permit) => (
                  <li key={listKey(permit)} className="pretty-li">
                    <span className="li-icon permit">
                      <FaFileCircleCheck />
                    </span>
                    <span>{permit}</span>
                    {renderItemApplyLink("permits", permit)}
                  </li>
                ))}
              </ul>
            </section>

            <section className="naics-section next-steps-panel next-card">
              <h4>
                <FaClipboardCheck className="title-icon" />
                Next Steps
              </h4>
              {renderPartLinks("next_steps")}
              <ol className="naics-list">
                {result.next_steps.map((step, index) => (
                  <li key={listKey(step)} className="pretty-li">
                    <span className="li-icon step">S{index + 1}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </section>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default NaicsComplianceChecker;
